<template>
  <div>
    <div
      class="pa-0 ml-auto mr-auto justify-center flex-column profile-data-sharing"
    >
      <div class="form-title mb-6">
        {{ t("Data sharing") }}
      </div>
      <div
        class="mb-8"
        v-html="t('At the end of each ordering process')"
      />
      <div>
        <InputText
          v-model="emailAdressToAdd"
          class="col-8 px-0"
          :label="t('Email address')"
          :rules="emailRules"
        />
        <div class="profile-data-sharing__btn">
          <Button
            color="primary"
            :disabled="!validEmail"
            @click="handleEmailAdressAdd()"
          >
            {{ t("Share orders") }}
          </Button>
        </div>
      </div>
      <div class="text-lg-h6 text-uppercase">
        {{ t("Quick Share List") }}
      </div>
      <ConfirmationDialog
        v-model="showDeleteDialog"
        :title="t('Delete - {email}', { email: deleteEmail })"
        :message="t('Are you sure you want to delete this user?')"
        @confirm="confirmDeleteSharedUser()"
        @cancel="showDeleteDialog = false"
      />

      <v-data-table
        :headers="headers"
        :items="sharedUsers"
        :items-per-page="-1"
        :no-data-text="t('No data available')"
        :mobile-breakpoint="-1"
        hide-default-footer
        class="share-order-table"
      >
        <template #item.email="{ item }">
          {{ item.email || item.doctorEmail }}
        </template>
        <template #item.name="{ item }">
          {{ item.firstName }} {{ item.lastName }}
        </template>
        <template #item.actions="{ item }">
          <Button
            color="error"
            @click="deleteSharedUser(item.email)"
          >
            {{ t("Delete") }}
          </Button>
        </template>

      </v-data-table>

      <div class="d-flex mt-8 mb-1 mt-4">
        <Button
          class="col mr-3"
          color="secondary"
          :scroll="true"
          @click="$emit('goto', 'notification')"
        >
          <v-icon start>
            mdi-chevron-left
          </v-icon>
          {{ t("Previous") }}
        </Button>
        <Button
          class="col"
          color="primary"
          :scroll="true"
          @click="$emit('goto', 'account-management')"
        >
          {{ t("Next") }}
          <v-icon end>
            mdi-chevron-right
          </v-icon>
        </Button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {ref, computed} from "vue"
import { useI18n } from "vue-i18n"
import Button from "@/components/input-components/Button.vue"
import InputText from "@/components/input-components/InputText.vue"
import ConfirmationDialog from "@/components/dialogs/ConfirmationDialog.vue"
import NotificationMessageService from "@/services/notificationmessage.service"
import { ProfileService } from "@/services/profile.service"
import { EMAIL_REG_EXP } from "@/utils/constants"
import { User } from '@/entities/user'

defineOptions({ name: "ProfileDataSharing" })

const { t } = useI18n()

const props = defineProps<{
  userProfile: {
    additionalEmail: string[]
  }
  sharedUsers: User[]
}>()

const emit = defineEmits<{
  goto: [section: string]
  reloadSharedUsers: []
}>()

const emailAdressToAdd = ref("")
const profileService = new ProfileService()
const showDeleteDialog = ref(false)
const deleteEmail = ref("")

const headers = computed(() => [
  {title: t("Title"), value: "title", sortable: false},
  {title: t("Name"), value: "name", sortable: false, width: "30%"},
  {title: t("Email"), value: "email", sortable: false, width: "65%"},
  {title: '', value: "actions", sortable: false},
]);

const emailRules = [
  (v: string) =>
    !v || EMAIL_REG_EXP.test(v) || t("This field must contain a valid email"),
]

const validEmail = computed(() => EMAIL_REG_EXP.test(emailAdressToAdd.value))

async function handleEmailAdressAdd() {
  if (emailAdressToAdd.value === props.userProfile.email) return;

  await profileService
    .postSharedUsers(emailAdressToAdd.value, "", "", "", "")
    .then(() => {
      NotificationMessageService.showSuccess(
        t("Shared user has been added succesful.")
      )
      emailAdressToAdd.value = ''
      emit("reloadSharedUsers")
    })
    .catch(() => {
      NotificationMessageService.showError(
        t("The e-mail you entered is already registered.")
      )
    })
}

function deleteSharedUser(email: string) {
  showDeleteDialog.value = true
  deleteEmail.value = email
}

function confirmDeleteSharedUser() {
  profileService
    .deleteSharedUsers(deleteEmail.value, "", "", "", "")
    .then(() => {
      NotificationMessageService.showSuccess(
        t("Shared user has been deleted succesful.")
      )
      emit("reloadSharedUsers")
      showDeleteDialog.value = false
    })
}
</script>

<style lang="scss">
.profile-data-sharing {
  max-width: 40rem;

  .mb-1 {
    width: 25rem;
  }

  .row {
    max-height: 44px;
  }

  td.v-data-table__td {
    word-break: break-all;
    white-space: normal;
  }

  &__btn {
    > button.v-btn.v-btn {
      margin-top: -1rem;
      margin-bottom: 2rem;
    }
  }
}
</style>
